import type { Pog } from "@CommonTypes/merchflow/pog/pog";

import type { PlanogramSelection } from "./types";

export type ItemPlacementSide = "left" | "right";

export interface ItemPlacementSourceSet {
  readonly activeItemId: string;
  readonly itemIds: readonly string[];
  readonly kind: "products";
}

export interface ItemPlacementTarget {
  readonly itemId: string;
  readonly kind: "item";
  readonly side: ItemPlacementSide;
}

export interface ItemPlacementOperation {
  readonly kind: "item-placement";
  readonly intent: "move" | "copy";
  readonly source: ItemPlacementSourceSet;
  readonly target: ItemPlacementTarget;
}

export interface ShelfPlacementSourceSet {
  readonly activeShelfId: string;
  readonly shelfIds: readonly string[];
  readonly kind: "shelves";
}

export interface ShelfPlacementTarget {
  readonly bayId: string;
  readonly kind: "shelf-position";
  readonly notchNo: number;
}

export interface ShelfPlacementOperation {
  readonly kind: "shelf-placement";
  readonly source: ShelfPlacementSourceSet;
  readonly target: ShelfPlacementTarget;
}

export interface PegboardPlacementSourceSet {
  readonly activePlacementId: string;
  readonly placementIds: readonly string[];
  readonly kind: "placements";
}

export interface PegboardPlacementTarget {
  readonly pegboardId: string;
  readonly holeNoLeft: number;
  readonly holeNoTop: number;
  readonly kind: "pegboard-hole";
}

export interface PegboardPlacementOperation {
  readonly kind: "pegboard-placement";
  readonly source: PegboardPlacementSourceSet;
  readonly target: PegboardPlacementTarget;
}

export interface ItemRangeSourceSet {
  readonly activeItemId: string;
  readonly itemIds: readonly string[];
  readonly kind: "range-items";
}

export type ItemRangeTarget =
  | { readonly itemId: string; readonly kind: "item"; readonly side: ItemPlacementSide }
  | { readonly kind: "shelf"; readonly shelfId: string };

/**
 * Ranging hand-off: items dropped onto the canvas from outside the stage
 * (e.g. the unranged-items panel) land next to a target item or at the end
 * of a shelf — the canvas equivalent of the DOM Dropper system.
 */
export interface ItemRangeOperation {
  readonly kind: "item-range";
  readonly source: ItemRangeSourceSet;
  readonly target: ItemRangeTarget;
}

/** Domain-space container bounds (y measured up from the planogram floor, like the DOM Draggable). */
export interface ContainerBounds {
  readonly xLeft: number;
  readonly xRight: number;
  readonly yBottom: number;
  readonly yTop: number;
}

export interface ContainerSourceSet {
  readonly containerId: string;
  readonly kind: "container";
}

export interface ContainerMoveOperation {
  readonly kind: "container-move";
  readonly source: ContainerSourceSet;
  readonly target: {
    readonly bounds: ContainerBounds;
    readonly kind: "container-bounds";
  };
}

export interface ContainerResizeOperation {
  readonly kind: "container-resize";
  readonly source: ContainerSourceSet;
  readonly target: {
    readonly bounds: ContainerBounds;
    readonly kind: "container-bounds";
  };
}

export interface ShelfResizeDimensions {
  readonly depth?: number;
  readonly thickness?: number;
  readonly width?: number;
}

export interface ShelfResizeOperation {
  readonly kind: "shelf-resize";
  readonly source: ShelfPlacementSourceSet;
  readonly target: {
    readonly dimensions: ShelfResizeDimensions;
    readonly kind: "shelf-size";
  };
}

export type PlanogramOperation =
  | ContainerMoveOperation
  | ContainerResizeOperation
  | ItemPlacementOperation
  | ItemRangeOperation
  | PegboardPlacementOperation
  | ShelfPlacementOperation
  | ShelfResizeOperation;

export type PlanogramMoveReasonCode =
  | "allowed"
  | "commit-failed"
  | "incompatible-source"
  | "incompatible-target"
  | "missing-source"
  | "missing-target"
  | "no-op"
  | "duplicate-source"
  | "empty-source"
  | "unsupported-source-set"
  | "self-target"
  | "snaking-unconfirmed"
  | "unsupported-fixtures"
  | "priority-order-violation"
  | "duplicate-cdt-path"
  | "priority-recalculation-failed"
  | "missing-shelf"
  | "duplicate-shelf"
  | "unsupported-movement"
  | "collision"
  | "invalid-resize"
  | "partial-mutation"
  | "invalid-hole";

export type PlanogramMoveCapability =
  | { readonly allowed: true; readonly reason: "allowed" }
  | {
      readonly allowed: false;
      readonly reason: Exclude<PlanogramMoveReasonCode, "allowed" | "commit-failed">;
    };

export type PlanogramMoveResult =
  | {
      readonly pog: Pog;
      readonly selection: PlanogramSelection;
      readonly status: "committed";
      readonly reason: "allowed";
    }
  | {
      readonly pog: Pog;
      readonly selection: PlanogramSelection;
      readonly status: "no-op";
      readonly reason: "no-op";
    }
  | {
      readonly pog: Pog;
      readonly selection: PlanogramSelection;
      readonly status: "rejected";
      readonly reason: Exclude<PlanogramMoveReasonCode, "allowed" | "no-op">;
    };

export interface PlanogramMoveCommit {
  readonly operation: PlanogramOperation;
  readonly pog: Pog;
  readonly selection: PlanogramSelection;
}

export interface PlanogramMoveResultEvent {
  readonly operation: PlanogramOperation | null;
  readonly result: PlanogramMoveResult;
}

export interface CreateItemPlacementOperationArgs {
  readonly activeItemId: string;
  readonly intent: "move" | "copy";
  readonly sourceItemIds: readonly string[];
  readonly side: ItemPlacementSide;
  readonly targetItemId: string;
}

export const createItemPlacementOperation = ({
  activeItemId,
  intent,
  sourceItemIds,
  side,
  targetItemId,
}: CreateItemPlacementOperationArgs): ItemPlacementOperation => ({
  intent,
  kind: "item-placement",
  source: {
    activeItemId,
    itemIds: sourceItemIds,
    kind: "products",
  },
  target: {
    itemId: targetItemId,
    kind: "item",
    side,
  },
});

export interface CreateItemRangeOperationArgs {
  readonly activeItemId: string;
  readonly sourceItemIds: readonly string[];
  readonly target: ItemRangeTarget;
}

export const createItemRangeOperation = ({
  activeItemId,
  sourceItemIds,
  target,
}: CreateItemRangeOperationArgs): ItemRangeOperation => ({
  kind: "item-range",
  source: {
    activeItemId,
    itemIds: sourceItemIds,
    kind: "range-items",
  },
  target,
});

export interface CreateContainerOperationArgs {
  readonly bounds: ContainerBounds;
  readonly containerId: string;
}

export const createContainerMoveOperation = ({
  bounds,
  containerId,
}: CreateContainerOperationArgs): ContainerMoveOperation => ({
  kind: "container-move",
  source: { containerId, kind: "container" },
  target: { bounds, kind: "container-bounds" },
});

export const createContainerResizeOperation = ({
  bounds,
  containerId,
}: CreateContainerOperationArgs): ContainerResizeOperation => ({
  kind: "container-resize",
  source: { containerId, kind: "container" },
  target: { bounds, kind: "container-bounds" },
});

export interface CreatePegboardPlacementOperationArgs {
  readonly activePlacementId: string;
  readonly holeNoLeft: number;
  readonly holeNoTop: number;
  readonly pegboardId: string;
  readonly placementIds: readonly string[];
}

export const createPegboardPlacementOperation = ({
  activePlacementId,
  holeNoLeft,
  holeNoTop,
  pegboardId,
  placementIds,
}: CreatePegboardPlacementOperationArgs): PegboardPlacementOperation => ({
  kind: "pegboard-placement",
  source: { activePlacementId, kind: "placements", placementIds },
  target: { holeNoLeft, holeNoTop, kind: "pegboard-hole", pegboardId },
});

export interface CreateShelfPlacementOperationArgs {
  readonly activeShelfId: string;
  readonly bayId: string;
  readonly notchNo: number;
  readonly shelfIds: readonly string[];
}

export const createShelfPlacementOperation = ({
  activeShelfId,
  bayId,
  notchNo,
  shelfIds,
}: CreateShelfPlacementOperationArgs): ShelfPlacementOperation => ({
  kind: "shelf-placement",
  source: { activeShelfId, kind: "shelves", shelfIds },
  target: { bayId, kind: "shelf-position", notchNo },
});

export interface CreateShelfResizeOperationArgs {
  readonly dimensions: ShelfResizeDimensions;
  readonly shelfIds: readonly string[];
  readonly activeShelfId?: string;
}

export const createShelfResizeOperation = ({
  activeShelfId,
  dimensions,
  shelfIds,
}: CreateShelfResizeOperationArgs): ShelfResizeOperation => ({
  kind: "shelf-resize",
  source: {
    activeShelfId: activeShelfId ?? shelfIds[0] ?? "",
    kind: "shelves",
    shelfIds,
  },
  target: { dimensions, kind: "shelf-size" },
});
